import { TenantSeniorityStatusEnum } from 'api/real-estate';
import { GenericMessage } from 'components/feedback/Message';
import { BaseSection, InputContainer } from 'components/layout/Section/BaseSection';
import { NsKey } from 'enums/NsKey';
import { InputFactory } from 'factories/InputFactory';
import { useRgiRiskData } from 'features/subscription-process/contexts/selectors/selectors';
import { useFormikContext } from 'formik';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useCallback, useMemo } from 'react';
import { tenantInputsConfig } from '../../form/tenantInputsConfig';
import { TenantFormFieldNames } from '../../types/tenantFormFieldNames';
import { TenantFormValues } from '../../types/tenantFormValues';
import { getTenantSeniorityStatus } from '../../utils/tenantSeniority';
import { TenantGuarantorSection } from './TenantGuarantorSection';

export const TenantLayout: React.FC = () => {
  const { t } = useDynamicTranslation([NsKey.RealEstate, NsKey.Common]);
  const { values } = useFormikContext<TenantFormValues>()
  const riskData = useRgiRiskData();

  // Statut d'ancienneté à la date de début du contrat : pilote les champs exigés.
  const seniorityStatus = useMemo(() =>
    getTenantSeniorityStatus(values[TenantFormFieldNames.CheckInDate], riskData?.contractStartDate),
    [values, riskData]
  )
  const isInPlace = seniorityStatus === TenantSeniorityStatusEnum.InPlace
  const isRefusedPeriod = seniorityStatus === TenantSeniorityStatusEnum.RefusedPeriod
  const isFutureEntry = seniorityStatus === TenantSeniorityStatusEnum.FutureEntry

  const isGuarantorRequired = useMemo(() => {
    const professionalSituation = values[TenantFormFieldNames.ProfessionalSituation]
    return professionalSituation === "Etudiant" && seniorityStatus === TenantSeniorityStatusEnum.New
  }, [values, seniorityStatus])

  const renderField = useCallback((fieldName: any) => {
    return (
      <InputFactory config={tenantInputsConfig} fieldName={fieldName} />
    );
  }, []);

  return <div className="font-lato flex flex-col gap-4">
    <BaseSection title={t('TENANT.SECTIONS.TENANT_INFOS')}>
      <div className='pt-2'>
        <InputContainer>
          <div className="w-full col-span-6 flex gap-4">
            <div className="w-32">
              {renderField(TenantFormFieldNames.Civility)}
            </div>
            <div className="flex-grow">
              {renderField(TenantFormFieldNames.LastName)}
            </div>
          </div>
          <div className="col-span-3">
            {renderField(TenantFormFieldNames.FirstName)}
          </div>
          <div className="col-span-3">
            {renderField(TenantFormFieldNames.BirthDate)}
          </div>
          <div className="col-span-3">
            {renderField(TenantFormFieldNames.Phone)}
          </div>
          <div className="col-span-3">
            {renderField(TenantFormFieldNames.Email)}
          </div>
          <div className="col-span-3">
            {renderField(TenantFormFieldNames.CheckInDate)}
          </div>
          {/* Locataire en place : plus de dossier pro/revenus, le champ disparaît. */}
          {!isInPlace &&
            <div className="col-span-3">
              {renderField(TenantFormFieldNames.ProfessionalSituation)}
            </div>}
        </InputContainer>
      </div>
    </BaseSection>
    {
      isRefusedPeriod &&
      <GenericMessage
        message={t('TENANT.LABELS.REFUSED_PERIOD_WARNING')}
        type="warning"
        dismissible={false}
      />
    }
    {
      isFutureEntry &&
      <GenericMessage
        message={t('TENANT.LABELS.FUTURE_ENTRY_WARNING')}
        type="warning"
        dismissible={false}
      />
    }
    {
      isInPlace &&
      <BaseSection title={t('TENANT.SECTIONS.RENT_PAYMENTS_SECTION')}>
        <div className='rounded-xl p-4 border shadow bg-white space-y-3'>
          <GenericMessage
            message={t('TENANT.LABELS.IN_PLACE_INFO')}
            type="info"
            dismissible={false}
          />
          {renderField(TenantFormFieldNames.RentPaymentsUpToDate)}
        </div>
      </BaseSection>
    }
    {
      !isInPlace && !isRefusedPeriod && !isFutureEntry &&
      <BaseSection title={t('TENANT.SECTIONS.INCOMES_SECTION')}>
        <div className='rounded-xl p-4 border shadow bg-white'>
          {renderField(TenantFormFieldNames.Incomes)}
        </div>
      </BaseSection>
    }
    {
      isGuarantorRequired &&
      <TenantGuarantorSection />
    }
  </div>
};
